import React, { useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { MDBInput } from "mdbreact";
import Button from "~/components/common/atoms/Button";
import voucher from "~/assets/img/svg/voucher.svg";
import { openToast } from "~/components/common/molecules/ToastNotification";
import styles from "./style.module.scss";

const LANGUAGES = [
  { key: "vi", label: "Tiếng Việt" },
  { key: "en", label: "Tiếng Anh/Hoa" },
  { key: "km", label: "Tiếng Khmer" }
];

function transformData(data) {
  const map = {};
  const normalizeLocale = locale => {
    return locale;
  };
  if (Array.isArray(data)) {
    data.forEach(item => {
      const key = normalizeLocale(item.locale);
      if (!map[key]) map[key] = [];
      map[key].push({
        ...item
      });
    });
  }
  for (const key in map) {
    map[key].sort((a, b) => a.id - b.id);
  }
  return map;
}

export default function Care247ServiceApply({
  loadingCare247Service,
  onApply,
  onApplyVoucher,
  dataCare247Services,
  dataCheckVoucherCare247,
  loadingCheckVoucherCare247,
  errorCheckVoucherCare247,
  billInfo
}) {
  const [language, setLanguage] = useState("vi");
  const [selected, setSelected] = useState(null);
  const [voucherCode, setVoucherCode] = useState("");
  const packages = useMemo(() => {
    const map = transformData(dataCare247Services?.addonServices || []);
    return map[language] || [];
  }, [dataCare247Services, language]);

  useEffect(() => {
    if (billInfo?.care247?.addonServices?.length > 0) {
      const care = billInfo?.care247?.addonServices?.[0];
      setLanguage(care.locale);
    }
  }, [billInfo]);

  const handleSelect = pkg => {
    setSelected(pkg?.id);
  };

  const handleApply = () => {
    if (!selected) return;
    const selectedPackage = packages.find(pkg => pkg.id === selected);
    if (onApply) onApply(selectedPackage);
  };

  const handleApplyVoucher = () => {
    if (!selected) {
      openToast("Bạn chưa chọn gói dịch vụ để áp dụng voucher!", "error");
      return;
    }
    if (!voucherCode) {
      openToast("Vui lòng nhập mã voucher!", "error");
      return;
    }
    const selectedPackage = packages.find(pkg => pkg.id === selected);
    if (onApplyVoucher) onApplyVoucher(selectedPackage, voucherCode);
  };

  const renderVoucherValue = voucher => {
    if (!voucher) return "";

    if (voucher.type === "fixed_amount") {
      return `${voucher.value.toLocaleString("vi-VN")} VNĐ`;
    }

    if (voucher.type === "percentage") {
      return `${voucher.value}%`;
    }

    return voucher.value;
  };

  const formatDateVoucher = dateStr => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("vi-VN");
  };

  return (
    <div className={styles.wrapper}>
      {/* --- Language Tabs --- */}
      <div className={styles.languageTabs}>
        {LANGUAGES.map(lang => (
          <button
            key={lang.key}
            className={cx(styles.langBtn, {
              [styles.active]: language === lang.key
            })}
            onClick={() => {
              setLanguage(lang.key);
              handleSelect(null);
            }}
          >
            {lang.label}
            {language === lang.key ? (
              <div className={styles.langBtnChecked}>
                <span>✔</span>
              </div>
            ) : null}
          </button>
        ))}
      </div>

      {/* --- Package List --- */}
      <div className={styles.packageList}>
        {loadingCare247Service ? (
          <>
            {[1, 2, 3].map(i => (
              <div key={i} className={styles.skeletonItem}>
                <div
                  className={styles.skeletonLine}
                  style={{ width: "100%" }}
                />
                <div className={styles.skeletonLine} style={{ width: "60%" }} />
              </div>
            ))}
          </>
        ) : packages && packages.length > 0 ? (
          <>
            {packages.map(pkg => (
              <label
                key={pkg.id}
                className={cx(styles.packageItem, {
                  [styles.selected]: selected === pkg.id
                })}
                onClick={() => handleSelect(pkg)}
              >
                {pkg?.preferentialPriceTimeDescription && (
                  <div div className={styles.badge}>
                    <img
                      src={voucher}
                      alt=""
                      width={10}
                      height={10}
                      layout="fixed"
                    />
                    <span>
                      {pkg?.preferentialPriceTimeDescription ||
                        "Ưu đãi khung giờ"}
                    </span>
                  </div>
                )}
                <MDBInput
                  checked={selected === pkg.id}
                  type="radio"
                  onClick={() => handleSelect(pkg)}
                />
                <div className={styles.info}>
                  <div className={styles.name}>{pkg.title}</div>
                  <div className={styles.price}>
                    <div className={styles.priceGroup}>
                      {pkg.originalPrice ? (
                        <span className={styles.originalPrice}>
                          {pkg.originalPrice.toLocaleString("vi-VN")}
                          {pkg.currency}
                        </span>
                      ) : null}
                      <span>
                        {pkg.price.toLocaleString("vi-VN")}
                        {pkg.currency}
                      </span>
                    </div>
                    {pkg.unitPrice ? (
                      <span className={styles.sub}>
                        ({pkg.unitPrice.toLocaleString("vi-VN")}
                        {pkg.currency}
                        /giờ)
                      </span>
                    ) : null}
                  </div>
                </div>
              </label>
            ))}
            <div className={styles.voucherInputWrapper}>
              <MDBInput
                label="Nhập mã voucher"
                type="text"
                value={voucherCode}
                onChange={e => setVoucherCode(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </div>
          </>
        ) : (
          <div className={styles.empty}>Không có gói dịch vụ</div>
        )}
      </div>
      {dataCheckVoucherCare247 && !errorCheckVoucherCare247 ? (
        <div className={styles.infoVoucherWrapper}>
          <div className={styles.item}>
            <div className={styles.label}>Giá trị:</div>
            <div className={cx(styles.value, styles.useValue)}>
              {renderVoucherValue(dataCheckVoucherCare247)}
            </div>
          </div>
          <div className={styles.item}>
            <div className={styles.label}>Thời gian áp dụng:</div>
            <div className={styles.value}>
              Từ {formatDateVoucher(dataCheckVoucherCare247.startDate)} đến{" "}
              {formatDateVoucher(dataCheckVoucherCare247.endDate)}
            </div>
          </div>
        </div>
      ) : errorCheckVoucherCare247 ? (
        <div className={cx(styles.infoVoucherWrapper, styles.error)}>
          {errorCheckVoucherCare247.error}
        </div>
      ) : null}
      <div className={styles.applyWrapper}>
        <Button
          create="create"
          className={cx(styles.applyVoucherBtn)}
          disabled={!selected || !(packages && packages.length > 0)}
          onClick={handleApplyVoucher}
        >
          <span>Áp dụng voucher</span>
        </Button>
        <Button
          create="create"
          className={cx(styles.applyBtn)}
          disabled={!selected || !(packages && packages.length > 0)}
          onClick={handleApply}
        >
          <span>Lấy liên kết</span>
        </Button>
      </div>
    </div>
  );
}
